








































































































































import { Component, Vue } from "vue-property-decorator";
import router from "@/router/router";
import { Role } from "@/data/models/Role";
import { UserSearchResult, UserSearchRow } from "@/data/models/SharedUsers";
import { cachedUserSearch, searchUsersForAdministration } from "@/data/repository/SharedUsersRepository";
import ContentStateComponent from "@/components/common/ContentState.vue";
import AdminRefreshButtonComponent from "@/components/admin/AdminRefreshButton.vue";
import MenuIconWithMultiSelectionListComponent from "@/components/common/MultiSelectionFilterMenu.vue";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { readThroughCache } from "@/utils/AdminReadCache";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { userStatus, UserStatusOption } from "@/utils/ClientUserList";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { formatUserRole } from "@/utils/LocalisationHelper";
import { ComponentName } from "@/utils/RouteGuards";
import { getValue, setValue } from "@/utils/SecureLocalStorage";
import { DEFAULT_USER_SEARCH_STATUSES, selectUserSearchRows, userSearchQuery } from "@/utils/SharedUserList";
import { themed } from "@/utils/Theme";
import { getLastVisitedItem, LAST_VISITED_ITEM_BACKGROUND_STYLE, setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";

/** Session storage key (see `SecureLocalStorage`) for the last search, so a new tab state starts with it. */
const SEARCH_STORAGE_KEY = "ADMIN_USER_SEARCH";
/** How long typing pauses before the search goes to IRES Cloud. */
const SEARCH_DELAY_MS = 500;

/**
 * Benutzer suchen in the IRES Admin Client: users across every client, deleted ones included and
 * dimmed (Taimoor, 2026-11-03), with their client and which kind of sharing they are in.
 *
 * **Kept alive** (`includeKeepAlive` in `Scaffold.vue`, the explicit `name`): back from a client shows
 * the rows at once and the opened one in view, then repeats the search quietly.
 *
 * **Not the access boundary**: `searchUsersForAdministration` repeats the portal and `MANAGE_CLIENTS`
 * checks and returns only the listed fields. The roles are shown by their standard names: a client's
 * own labels would need its role settings per row.
 */
@Component({
  name: "AdminUserSearch",
  components: {
    "multi-selection-menu-icon": MenuIconWithMultiSelectionListComponent,
    "content-state": ContentStateComponent,
    "admin-refresh-button": AdminRefreshButtonComponent,
  },
})
export default class AdminUserSearch extends Vue {
  input = "";
  /** The search the rows answer, null before the first one. */
  shownQuery: string | null = null;
  result: UserSearchResult | null = null;
  statuses: UserStatusOption[] = [...DEFAULT_USER_SEARCH_STATUSES];
  isLoading = false;
  /** Showing the cached rows while IRES Cloud is asked again (`AdminReadCache.ts`). */
  isRefreshing = false;
  loadError = false;
  lastVisitedKey: string | null = null;
  /** Gates the app-bar portal: a cached instance must not fill the bar of another screen. */
  componentActive = false;
  searchTimer: number | null = null;

  readonly emptyValue = EMPTY_VALUE;
  readonly statusOptions = DEFAULT_USER_SEARCH_STATUSES;
  readonly defaultStatuses = DEFAULT_USER_SEARCH_STATUSES;

  get rows(): UserSearchRow[] {
    return this.result ? selectUserSearchRows(this.result.users, this.statuses) : [];
  }

  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  /** IRES Cloud sends the first 100 matches only. */
  get isCapped(): boolean {
    return !this.isLoading && !!this.result && this.result.total > this.result.users.length;
  }

  get emptyText(): string {
    if (this.shownQuery === null) {
      return this.$t("admin.userSearch.hint").toString();
    }
    return this.rows.length === 0 ? this.$t("admin.userSearch.none").toString() : "";
  }

  get filterDescription(): string {
    if (this.statuses.length === DEFAULT_USER_SEARCH_STATUSES.length) {
      return "";
    }
    return DEFAULT_USER_SEARCH_STATUSES.filter((status) => this.statuses.includes(status))
      .map((status) => this.statusName(status))
      .join(", ");
  }

  /** Phones (< 600 px, ui-conventions.md). */
  get isPhone(): boolean {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The grey line under a phone's title line: the cap and the filters, whichever apply. */
  get phoneHeaderDetails(): string {
    return [
      this.isCapped && this.result ? this.$t("admin.userSearch.capped", [this.result.users.length, this.result.total]).toString() : null,
      this.filterDescription || null,
    ]
      .filter((part) => part !== null)
      .join(" · ");
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  statusName(status: UserStatusOption): string {
    return this.$t(`admin.clientUsers.statuses.${status}`).toString();
  }

  statusKey(status: UserStatusOption): string {
    return status;
  }

  statusLabel(user: UserSearchRow): string {
    return this.statusName(userStatus(user));
  }

  roleLabel(user: UserSearchRow): string {
    return user.role ? formatUserRole(this, user.role as Role) || user.role : EMPTY_VALUE;
  }

  fullName(user: UserSearchRow): string {
    return [user.firstName, user.lastName].filter((part) => !!part).join(" ") || user.nickname || EMPTY_VALUE;
  }

  /** "Kurzname JK · Personalnummer 12345", whichever the user has. */
  nicknameLine(user: UserSearchRow): string {
    return [
      user.nickname ? this.$t("admin.userSearch.nickname", [user.nickname]).toString() : null,
      user.staffNumber ? this.$t("admin.userSearch.staffNumber", [user.staffNumber]).toString() : null,
    ]
      .filter((part) => part !== null)
      .join(" · ");
  }

  /** "Herrmann GmbH (10079)", the client as a phone row names it. */
  clientLine(user: UserSearchRow): string {
    const name = user.clientName || EMPTY_VALUE;
    return user.clientNumber ? `${name} (${user.clientNumber})` : name;
  }

  /** A phone row's last line: short name and staff number, role, status. */
  phoneDetails(user: UserSearchRow): string {
    return [this.nicknameLine(user) || null, this.roleLabel(user), this.statusLabel(user)]
      .filter((part) => part !== null)
      .join(" · ");
  }

  /** A group user has one row per client, so the client is part of the key. */
  rowKey(user: UserSearchRow): string {
    return `${user.clientId}/${user.id}`;
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(user: UserSearchRow, hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    if (background) {
      return `background-color: ${background}`;
    }
    return this.rowKey(user) === this.lastVisitedKey ? LAST_VISITED_ITEM_BACKGROUND_STYLE : "";
  }

  onInput(value: string | null) {
    this.input = value ?? "";
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
    }
    this.searchTimer = window.setTimeout(() => this.runSearch(), SEARCH_DELAY_MS);
  }

  /** Sends the search when it is long enough and differs from the one shown. */
  runSearch() {
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    const query = userSearchQuery(this.input);
    setValue(SEARCH_STORAGE_KEY, this.input);
    if (query === null) {
      this.shownQuery = null;
      this.result = null;
      this.loadError = false;
      return;
    }
    if (query !== this.shownQuery || this.loadError) {
      this.load(query);
    }
  }

  refresh() {
    if (this.shownQuery !== null) {
      this.load(this.shownQuery);
    }
  }

  /** The cached answer at once when this search was made before in this tab, then IRES Cloud's. */
  async load(query: string) {
    const cached = cachedUserSearch(query);
    this.shownQuery = query;
    this.isLoading = cached === null;
    this.isRefreshing = cached !== null;
    this.loadError = false;
    try {
      await readThroughCache(cached, () => searchUsersForAdministration(query), (result) => {
        // A slower answer to an older search must not replace a newer one.
        if (this.shownQuery === query) {
          this.result = result;
        }
      });
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and the refresh button retries.
      if (this.shownQuery === query) {
        this.loadError = true;
        this.result = null;
      }
    } finally {
      if (this.shownQuery === query) {
        this.isLoading = false;
        this.isRefreshing = false;
      }
    }
    this.scrollToLastVisited();
  }

  openClient(user: UserSearchRow) {
    this.lastVisitedKey = this.rowKey(user);
    setLastVisitedItem(this.lastVisitedKey, StorageKeyLastVisitedItemLevel.LEVEL_1);
    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_DETAIL, query: { clientId: user.clientId } });
  }

  openShared(user: UserSearchRow, tab: "emailAddresses" | "group") {
    router.push({ name: ComponentName.ADMIN_SHARED_USERS, query: { tab, search: user.emailAddress ?? "" } });
  }

  async scrollToLastVisited() {
    if (!this.lastVisitedKey) {
      return;
    }
    await this.$nextTick();
    const row = Array.from(this.$el.querySelectorAll<HTMLElement>("[data-row-key]")).find(
      (element) => element.dataset.rowKey === this.lastVisitedKey
    );
    row?.scrollIntoView({ block: "center" });
  }

  mounted() {
    this.componentActive = true;
    ToolbarContentModule.setI18nResource("admin.userSearch.title");
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.input = getValue(SEARCH_STORAGE_KEY) ?? "";
    this.runSearch();
  }

  /** Back from a client: rows at once, the opened one in view, then the same search quietly again. */
  activated() {
    this.componentActive = true;
    ToolbarContentModule.setI18nResource("admin.userSearch.title");
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.scrollToLastVisited();
    if (this.shownQuery !== null && !this.isLoading && !this.isRefreshing) {
      this.load(this.shownQuery);
    }
  }

  deactivated() {
    this.componentActive = false;
  }

  beforeDestroy() {
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
    }
  }
}
